import WindowedModalV2, { WindowedModalButton, WindowedModalButtonBar } from "../windowed-modal/windowed-modal";
import { AppPlanInfo, PlanPrivacy, PlanType, SubscriptionType } from "common/app-plans";
import SubscriptionToggle from "./private/subscription-toggle";
import AppPlans from "./private/app-plans";
import { useCallback, useEffect, useState } from "react";
import AppPlanBilling, { BillingView } from "./private/app-plan-billing";
import { defined, dontAwait } from "common/Support";
import { styled } from "sharedUI/styles";
import AppPlanReview from "./app-plan-review";
import { CardPreview } from "common/firebase-function-types";
import { GlideFC } from "lib/glide-fc";
import { useAppID } from "hooks/use-app-id";
import { useBuilderSelector } from "hooks/use-builder-selector";
import { getCurrentAppAndState } from "store/types";
import { shallowEqual } from "react-redux";
import { Owner } from "common/Database";
import UpgradeSuccessModal from "./upgrade-success-modal";
import { useHistory } from "react-router-dom";
import { appSettings } from "routes";
import { CardInfo } from "designer/modals/smart/payment-info-modal/payment-info";
import {
    confirmCardSetup,
    getPaymentMethodForStripeCard,
    getPaymentSetupIntent,
    setNewDefaultPaymentMethod,
} from "designer/modals/smart/payment-info-modal/payment-info-utils";
import { CardState, getCardState } from "designer/builder/ui/smart/unified-app-plan/app-settings-plan-lib";

enum UpgradeStage {
    Plan,
    Billing,
    Review,
    Success,
}

const Title = styled.div`
    max-width: 550px;
    overflow: hidden;
    text-overflow: ellipsis;

    font-size: 18px;
    font-weight: 800;
    color: ${p => p.theme.textDark};
    .secondary {
        opacity: 0.5;
    }
`;

const UpgradeBody = styled.div<{ stage: UpgradeStage }>`
    width: ${p => (p.stage === UpgradeStage.Success ? 336 : 796)}px;
    display: flex;
    justify-content: center;
`;

const TitleAccessoryWrapper = styled.div`
    margin-right: 20px;
`;

interface Props {
    // Note: This is used from the panel to automatically go to the confirm stage and disable navigation
    // `false` will start the whole flow
    fromPanel: boolean;
    upgradeComplete: boolean;
    owner: Owner;
    applyingNewPlan: boolean;
    appPlanInfo: AppPlanInfo;
    estimate: number | undefined;
    cardPreview: CardPreview | undefined;
    showProOnly: boolean;
    plan: PlanType | undefined;
    subscription: SubscriptionType;
    privacy: PlanPrivacy;
    numPrivateUsers: number;
    planChange: (plan: PlanType) => void;
    subscriptionChange: (sub: SubscriptionType) => void;
    privacyChange: (privacy: PlanPrivacy) => void;
    numPrivateUsersChange: (numUsers: number) => void;
    onClosed: () => void;
    onCompleted: () => void;
    onCardUpdated: () => void;
    onPromoCodeApplied: () => void;
}

export const UpgradeModal: GlideFC<Props> = p => {
    const {
        fromPanel,
        upgradeComplete,
        applyingNewPlan,
        appPlanInfo,
        plan,
        privacy,
        subscription,
        numPrivateUsers,
        planChange,
        privacyChange,
        subscriptionChange,
        numPrivateUsersChange,
        estimate,
        onClosed,
        onCompleted,
        cardPreview,
        owner,
        onCardUpdated,
        onPromoCodeApplied,
        showProOnly,
    } = p;

    const history = useHistory();
    const appID = useAppID();
    const { appTitle, appIcon, appAccentColor } = useBuilderSelector(s => {
        const serializedApp = getCurrentAppAndState(appID, s.apps)?.serializedApp;
        if (serializedApp === undefined) return {};
        return {
            appTitle: serializedApp.title,
            appIcon: serializedApp.iconImage,
            appAccentColor: serializedApp.theme.primaryAccentColor,
        };
    }, shallowEqual);

    const [stage, setStage] = useState(fromPanel ? UpgradeStage.Review : UpgradeStage.Plan);
    const [billingView, setBillingView] = useState(BillingView.Loading);

    const [paymentSetupIntent, setPaymentSetupIntent] = useState<string>();
    const [newCardPaymentInfo, setNewCardPaymentInfo] = useState<CardInfo>();
    const [newStripeCardInfo, setNewStripeCardInfo] = useState<any>();
    const [canAcceptNewCard, setCanAcceptNewCard] = useState(false);
    const [addingNewCard, setAddingNewCard] = useState(false);
    const [cardError, setCardError] = useState(false);

    const [paymentMethodId, setPaymentMethodId] = useState<string | undefined>(undefined);

    const navigateToPlan = useCallback(() => {
        setStage(UpgradeStage.Plan);
    }, []);

    const navigateToBilling = useCallback(() => {
        setStage(UpgradeStage.Billing);
    }, []);

    const navigateToReview = useCallback(() => {
        setStage(UpgradeStage.Review);
    }, []);

    const navigateToPrivacySettings = useCallback(() => {
        if (appID === undefined) return;
        history.push(appSettings(appID, "privacy"));
        onClosed();
    }, [appID, history, onClosed]);

    const cardState = getCardState(cardPreview);

    useEffect(() => {
        if (fromPanel && !upgradeComplete && cardState === CardState.CardPresent) {
            setStage(UpgradeStage.Review);
        } else if (fromPanel && !upgradeComplete && cardState === CardState.NoCard) {
            setStage(UpgradeStage.Billing);
        } else if (upgradeComplete) {
            setStage(UpgradeStage.Success);
        }
    }, [cardState, fromPanel, upgradeComplete]);

    useEffect(() => {
        if (cardState === CardState.NoCard) {
            setBillingView(BillingView.CardEntry);
        } else if (cardState === CardState.CardPresent) {
            setBillingView(BillingView.Summary);
        } else if (cardState === CardState.Loading) {
            setBillingView(BillingView.Loading);
        }
    }, [cardPreview, cardState]);

    useEffect(() => {
        // We only want to set this when the payment method has not been set in the parent
        // which controls the payment method creation
        if (billingView === BillingView.CardEntry && paymentSetupIntent === undefined) {
            async function getPaymentIntent() {
                const newSetupIntentResult = await getPaymentSetupIntent(owner.id);
                if (newSetupIntentResult.isErr()) {
                    setCardError(true);
                    return;
                }
                if (newSetupIntentResult.isOk()) {
                    setPaymentSetupIntent(newSetupIntentResult.value);
                }
            }
            dontAwait(getPaymentIntent());
        }
    }, [billingView, owner.id, paymentSetupIntent]);

    const updatePaymentInfo = useCallback((info: CardInfo, stripeCard: any) => {
        setCardError(false);
        setNewCardPaymentInfo(info);
        setNewStripeCardInfo(stripeCard);
    }, []);

    const onPayButton = useCallback(
        async (id: string) => {
            if (paymentSetupIntent !== undefined) {
                const cardSetupRes = await confirmCardSetup(paymentSetupIntent, id);
                if (cardSetupRes.isErr()) {
                    setCardError(true);
                    setAddingNewCard(false);
                    return false;
                } else {
                    const paymentID = cardSetupRes.value;
                    setPaymentMethodId(paymentID);
                    return true;
                }
            } else {
                return false;
            }
        },
        [paymentSetupIntent]
    );

    const addNewCard = useCallback(async () => {
        if (
            paymentSetupIntent === undefined ||
            !canAcceptNewCard ||
            newCardPaymentInfo === undefined ||
            newStripeCardInfo === undefined
        )
            return;

        setAddingNewCard(true);
        const paymentMethodRes = await getPaymentMethodForStripeCard(newCardPaymentInfo, newStripeCardInfo);
        if (paymentMethodRes.isErr()) {
            setCardError(true);
            setAddingNewCard(false);
            return;
        }

        const cardSetupRes = await confirmCardSetup(paymentSetupIntent, paymentMethodRes.value);
        if (cardSetupRes.isErr()) {
            setCardError(true);
            setAddingNewCard(false);
            return;
        } else {
            const paymentID = cardSetupRes.value;
            setPaymentMethodId(paymentID);
        }

        // Trigger a new preview charges to get the new card info in the summary
        setAddingNewCard(false);
        onCardUpdated();
    }, [canAcceptNewCard, newCardPaymentInfo, newStripeCardInfo, onCardUpdated, paymentSetupIntent]);

    /**
     * This is utter crap. We shouldn't depend on an effect to do this.
     * consider restructuring.
     */
    useEffect(() => {
        const fn = async () => {
            if (
                billingView === BillingView.CardEntry &&
                paymentSetupIntent !== undefined &&
                paymentMethodId !== undefined
            ) {
                setBillingView(BillingView.Loading);
                await setNewDefaultPaymentMethod(owner.id, paymentMethodId);

                // Trigger a new preview charges to get the new card info in the summary
                setPaymentMethodId(undefined);
                setPaymentSetupIntent(undefined);
                setAddingNewCard(false);
                onCardUpdated();
            }
        };

        void fn();
    }, [billingView, onCardUpdated, owner.id, paymentMethodId, paymentSetupIntent]);

    // NOTE: used to make windowed modal button api happy
    const noOp = useCallback(() => undefined, []);

    const onClose = useCallback(() => {
        if (applyingNewPlan) return;
        onClosed();
    }, [applyingNewPlan, onClosed]);

    if (appTitle === undefined || appIcon === undefined || appAccentColor === undefined || appID === undefined)
        return null;

    let currentTitle = <></>;
    let currentTitleAccessory = <></>;
    let currentView = <></>;
    let currentNavigation = <></>;
    if (stage === UpgradeStage.Plan) {
        currentTitle = <Title>Upgrade your {appTitle} App</Title>;
        currentTitleAccessory = (
            <TitleAccessoryWrapper>
                <SubscriptionToggle susbscription={subscription} subscriptionChange={subscriptionChange} />
            </TitleAccessoryWrapper>
        );
        currentView = (
            <AppPlans
                appIcon={appIcon}
                appAccentColor={appAccentColor}
                plan={plan}
                privacy={privacy}
                subscription={subscription}
                planChange={planChange}
                privacyChange={privacyChange}
                showProOnly={showProOnly}
            />
        );
        currentNavigation = (
            <WindowedModalButtonBar
                align="split"
                buttons={[
                    {
                        title: "Cancel",
                        type: "secondary",
                        variant: "default",
                        onClick: onClosed,
                    },
                    {
                        title: "Continue to Billing",
                        type: "primary",
                        variant: "default",
                        onClick: navigateToBilling,
                        disabled: plan === undefined || plan === PlanType.Free,
                    },
                ]}
            />
        );
    } else if (stage === UpgradeStage.Billing) {
        currentTitle = (
            <Title>
                <span className="secondary">Step 2 of 3:</span> Billing Info
            </Title>
        );
        currentTitleAccessory = <></>;
        currentView = (
            <AppPlanBilling
                billingView={billingView}
                updateBillingView={setBillingView}
                fromPanel={fromPanel}
                appPlanInfo={appPlanInfo}
                appIcon={appIcon}
                appAccentColor={appAccentColor}
                plan={defined(plan, "You should not be able to progress to billing with undefined plan")}
                privacy={privacy}
                subscription={subscription}
                numPrivateUsers={numPrivateUsers}
                privateUsersChange={numPrivateUsersChange}
                subscriptionChange={subscriptionChange}
                estimate={estimate}
                cardPreview={cardPreview}
                owner={owner}
                paymentSetupIntent={paymentSetupIntent}
                updatePaymentSetupIntent={setPaymentSetupIntent}
                updatePaymentInfo={updatePaymentInfo}
                updateCanAcceptNewCard={setCanAcceptNewCard}
                onPromoCodeApplied={onPromoCodeApplied}
                cardError={cardError}
                onPayButton={onPayButton}
            />
        );

        const canPerformClick =
            (billingView === BillingView.CardEntry && canAcceptNewCard) ||
            (billingView === BillingView.Summary && cardState === CardState.CardPresent);

        // Only allow progression to review if less than or equal to 250 users
        let nextButtontitle = "Continue to Review";
        let nextButtonDisabled = false;
        let href = undefined;
        let nextButtonAction = navigateToReview;
        if (numPrivateUsers >= 250 && billingView !== BillingView.CardEntry) {
            // no-op for contact sales since mailto is handled in the a tag
            nextButtonAction = noOp;
            nextButtontitle = "Contact Sales";
            href = "mailto:sales@glideapps.com";
        } else if (billingView === BillingView.CardEntry) {
            nextButtonAction = addNewCard;
            if (addingNewCard) {
                nextButtontitle = "Adding card";
                nextButtonDisabled = true;
            }
        }
        const nextDisabled = !canPerformClick || nextButtonDisabled;

        const billingButtons: readonly WindowedModalButton[] = [
            {
                title: "Back",
                type: "secondary",
                variant: "default",
                onClick: navigateToPlan,
            },
            {
                title: nextButtontitle,
                type: "primary",
                variant: "default",
                onClick: nextButtonAction,
                disabled: nextDisabled,
                href,
            },
        ];

        currentNavigation = <WindowedModalButtonBar align="split" buttons={billingButtons} />;
    } else if (stage === UpgradeStage.Review) {
        currentTitle = fromPanel ? (
            <Title>Review and Confirm</Title>
        ) : (
            <Title>
                <span className="secondary">Step 3 of 3:</span> Review and Confirm
            </Title>
        );
        currentTitleAccessory = <></>;
        currentView = (
            <AppPlanReview
                fromPanel={fromPanel}
                appPlanInfo={appPlanInfo}
                appName={appTitle}
                appIcon={appIcon}
                appAccentColor={appAccentColor}
                plan={defined(plan, "You should not be able to progress to confirm with undefined plan")}
                privacy={privacy}
                subscription={subscription}
                numPrivateUsers={numPrivateUsers}
                estimate={estimate}
                subscriptionChange={subscriptionChange}
                privateUsersChange={numPrivateUsersChange}
                goToBilling={navigateToBilling}
                cardPreview={cardPreview}
                owner={owner}
                onPromoCodeApplied={onPromoCodeApplied}
            />
        );
        currentNavigation = (
            <WindowedModalButtonBar
                align="split"
                buttons={[
                    {
                        title: fromPanel ? "Cancel" : "Back",
                        type: "secondary",
                        variant: "default",
                        onClick: fromPanel ? onClosed : navigateToBilling,
                    },
                    {
                        title: applyingNewPlan ? "Applying..." : "Complete Purchase",
                        type: "primary",
                        variant: "default",
                        disabled: applyingNewPlan,
                        onClick: onCompleted,
                    },
                ]}
            />
        );
    } else if (stage === UpgradeStage.Success) {
        currentTitle = <Title>Upgrade Complete</Title>;
        currentTitleAccessory = <></>;
        currentView = (
            <UpgradeSuccessModal
                appTitle={appTitle}
                appPlanCode={appPlanInfo.code}
                appAccentColor={appAccentColor}
                appIcon={appIcon}
                onNavigateToPrivacySettings={navigateToPrivacySettings}
            />
        );
        currentNavigation = (
            <WindowedModalButtonBar
                align="left"
                buttons={[
                    {
                        title: "Close",
                        type: "secondary",
                        variant: "default",
                        fullSize: true,
                        onClick: onClosed,
                    },
                ]}
            />
        );
    }

    return (
        <WindowedModalV2
            canClose={true}
            onClose={onClose}
            title={currentTitle}
            titleAccessory={currentTitleAccessory}
            footer={currentNavigation}
            footerDivider={stage === UpgradeStage.Success ? false : true}
            footerTopMargin={stage === UpgradeStage.Success ? 0 : 26}>
            <UpgradeBody stage={stage}>{currentView}</UpgradeBody>
        </WindowedModalV2>
    );
};
